import i18next, { type i18n as I18nInstance } from 'i18next';
import ICU from 'i18next-icu';
import { initReactI18next } from 'react-i18next';
import { DEFAULT_LOCALE, LOCALES, findLocale, negotiateLocale } from '@jetopolis/i18n';
import { ensureScriptFont } from './fonts';

/**
 * Инициализация i18next с ICU.
 *
 * keySeparator: false обязателен - ключи плоские вида namespace.section.key,
 * иначе i18next будет искать вложенный объект по точкам и вернёт undefined.
 * Под ICU не работают ни двойные скобки, ни суффиксы _one/_other: множественное
 * число целиком живёт внутри строки, и для русского, польского и арабского это
 * принципиально - категории задаёт целевой язык.
 */
const bundles = import.meta.glob('../../../../packages/i18n/locales/*.json');

async function loadBundle(code: string): Promise<Record<string, string>> {
  const key = Object.keys(bundles).find((p) => p.endsWith(`/${code}.json`));
  const loader = key === undefined ? undefined : bundles[key];
  if (!loader) throw new Error(`нет словаря для ${code}`);
  const mod = (await loader()) as { default: Record<string, string> };
  return mod.default;
}

/**
 * Словарь языка либо английский запасной.
 *
 * Недостающий или битый словарь НЕ имеет права оставить игрока с пустым
 * экраном: интерфейс на английском лучше, чем его отсутствие.
 */
async function loadBundleOrFallback(
  code: string,
): Promise<{ code: string; dict: Record<string, string> }> {
  try {
    return { code, dict: await loadBundle(code) };
  } catch (e) {
    console.error(`[i18n] словарь ${code} не загрузился, показываем ${DEFAULT_LOCALE}`, e);
    return { code: DEFAULT_LOCALE, dict: await loadBundle(DEFAULT_LOCALE) };
  }
}

const LOCALE_COOKIE = 'jt_locale';

function readCookie(name: string): string | undefined {
  const hit = document.cookie.split('; ').find((c) => c.startsWith(`${name}=`));
  return hit?.slice(name.length + 1);
}

/** Порядок определения локали: ?lng -> cookie -> язык браузера -> английский (04 §6.2). */
export function detectLocale(): string {
  const fromQuery = new URLSearchParams(window.location.search).get('lng');
  if (fromQuery && findLocale(fromQuery)) return findLocale(fromQuery)?.code ?? DEFAULT_LOCALE;
  const fromCookie = readCookie(LOCALE_COOKIE);
  if (fromCookie && findLocale(fromCookie)) return findLocale(fromCookie)?.code ?? DEFAULT_LOCALE;
  return negotiateLocale([...navigator.languages]).code;
}

/** Направление письма и скрипт проставляются на <html>: от них зависят и раскладка, и шрифт. */
export async function applyLocaleToDocument(code: string): Promise<void> {
  const meta = findLocale(code) ?? findLocale(DEFAULT_LOCALE);
  if (!meta) return;
  document.documentElement.lang = meta.code;
  document.documentElement.dir = meta.dir;
  document.documentElement.dataset['script'] = meta.script;
  await ensureScriptFont(meta.script);
}

export async function initI18n(): Promise<I18nInstance> {
  const wanted = detectLocale();
  const loaded = await loadBundleOrFallback(wanted);
  const code = loaded.code;
  const resources = loaded.dict;
  const fallback = code === DEFAULT_LOCALE ? resources : await loadBundle(DEFAULT_LOCALE);

  await i18next
    .use(ICU)
    .use(initReactI18next)
    .init({
      lng: code,
      fallbackLng: DEFAULT_LOCALE,
      supportedLngs: LOCALES.map((l) => l.code),
      resources: {
        [code]: { translation: resources },
        [DEFAULT_LOCALE]: { translation: fallback },
      },
      keySeparator: false,
      nsSeparator: false,
      interpolation: { escapeValue: false },
      returnNull: false,
    });

  await applyLocaleToDocument(code);
  return i18next;
}

export async function changeLocale(code: string): Promise<void> {
  if (!i18next.hasResourceBundle(code, 'translation')) {
    const loaded = await loadBundleOrFallback(code);
    // Если словаря нет, остаёмся на текущем языке, а не переключаемся молча
    // на английский: молчаливая подмена выглядит как поломка переключателя.
    if (loaded.code !== code) return;
    i18next.addResourceBundle(code, 'translation', loaded.dict);
  }
  await i18next.changeLanguage(code);
  await applyLocaleToDocument(code);
  // Год - максимум допустимого срока, локаль не тайна и не персональные данные.
  document.cookie = `${LOCALE_COOKIE}=${code}; path=/; max-age=31536000; samesite=lax`;
}

export { i18next };
